import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getDefaultStore } from 'jotai';
import { currentUserAtom, authLoadedAtom } from '../auth/AuthAtoms';
import { exchangeCodeForTokens, fetchUserDetails, linkSocialProvider } from '../api/auth';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/LoadingSpinner';

const store = getDefaultStore();

const AuthCallbackPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const processedRef = useRef(false);

    useEffect(() => {
        // Prevent double-execution in StrictMode
        if (processedRef.current) return;
        processedRef.current = true;

        const handleCallback = async () => {
            const code = searchParams.get('code');
            const state = searchParams.get('state');
            const error = searchParams.get('error');
            const errorDescription = searchParams.get('error_description');

            // Handle OAuth errors from Cognito
            if (error) {
                toast.error(errorDescription || error);
                cleanup();
                navigate('/login');
                return;
            }

            if (!code || !state) {
                toast.error('Invalid callback — missing authorization code or state');
                cleanup();
                navigate('/login');
                return;
            }

            // CSRF verification
            const savedState = sessionStorage.getItem('oauth_state');
            if (state !== savedState) {
                toast.error('Security check failed — state mismatch. Please try again.');
                cleanup();
                navigate('/login');
                return;
            }

            try {
                // Exchange code for Cognito tokens
                const tokens = await exchangeCodeForTokens(code);
                const intent = sessionStorage.getItem('oauth_intent') || 'login';

                if (intent === 'link') {
                    // IMPORTANT: Do NOT overwrite localStorage tokens during link flow.
                    // The user is already logged in — their existing tokens must be preserved.
                    // We only need the id_token from the exchange to prove the Google identity.
                    const linkResult = await linkSocialProvider('google', tokens.id_token, null, 'cognito');
                    if (linkResult.user) {
                        store.set(currentUserAtom, linkResult.user);
                    }
                    toast.success('Google account linked successfully');
                    cleanup();
                    navigate('/account');
                    return;
                }

                // Login/signup flow — store tokens
                // OAuth users: use id_token as Bearer token (contains identities claim
                // needed by middleware for federated user lookup)
                // Password users use Cognito AccessToken which has cognitoId in sub
                localStorage.setItem('accessToken', tokens.id_token);
                localStorage.setItem('idToken', tokens.id_token);
                if (tokens.refresh_token) {
                    localStorage.setItem('refreshToken', tokens.refresh_token);
                }

                // Fetch user details using the id_token
                const userDetails = await fetchUserDetails(tokens.id_token);

                if (userDetails?.user) {
                    // Existing user — set state and go home
                    store.set(currentUserAtom, userDetails.user);
                    store.set(authLoadedAtom, true);
                    localStorage.setItem('username', userDetails.user.username);
                    localStorage.setItem('authOrigin', 'cognito-oauth');
                    toast.success('Login successful');
                    cleanup();
                    navigate('/');
                } else {
                    // No user yet — needs username setup
                    cleanup();
                    navigate('/auth/choose-username');
                }
            } catch (err) {
                toast.error('Sign in failed: ' + (err.message || 'Authentication failed'));
                cleanup();
                navigate('/login');
            }
        };

        handleCallback();
    }, []);

    return <LoadingSpinner />;
};

function cleanup() {
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_intent');
    sessionStorage.removeItem('oauth_code_verifier');
}

export default AuthCallbackPage;
